import { useState } from "react";
import { Input } from "@/components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";

type CardInfo = {
  cardNumber: string;
  cardCode: string;
  expMonth: string;
  expYear: string;
  address: string;
  zip: string;
  fullName: string;
  country: string;
};

export default function AuthorizeNetCard({
  handleToken,
  handleBillingInfo,
  authorizeLoginId,
  authorizeClientKey,
}: {
  handleToken: (token: string) => void;
  handleBillingInfo: (billingInfo: {
    address?: string;
    name?: string;
    zip?: string;
    country?: string;
  }) => void;
  authorizeLoginId: string;
  authorizeClientKey: string;
}) {
  const { toast } = useToast();
  const [card, setCard] = useState<CardInfo>({
    cardNumber: "",
    cardCode: "",
    expMonth: "",
    expYear: "",
    address: "",
    zip: "",
    fullName: "",
    country: "US",
  });

  type ErrorMessage = { code: string; text: string };
  type DispatchDataResponse = {
    opaqueData: {
      dataDescriptor: string;
      dataValue: string;
    };
    messages: {
      resultCode: "Ok" | "Error";
      message: ErrorMessage[];
    };
  };

  const tokenizeCard = async (cardData: CardInfo) => {
    const auth = {
      clientKey: authorizeClientKey,
      apiLoginID: authorizeLoginId,
    };

    // @ts-expect-error - Accept is a global object provided by Authorize.net script
    if (window.Accept) {
      // @ts-expect-error - dispatchData is a method on the Accept object
      await window.Accept.dispatchData(
        {
          cardData: {
            cardNumber: cardData.cardNumber.replace(/\s/g, ""), // remove spaces
            month: cardData.expMonth,
            year: cardData.expYear,
            cardCode: cardData.cardCode,
            fullName: cardData.fullName,
            // address: cardData.address, # accept.js does not support this
            zip: cardData.zip,
            country: "US",
          },
          authData: auth,
        },
        (response: DispatchDataResponse) => {
          if (response.messages.resultCode === "Ok") {
            handleToken(response.opaqueData.dataValue);
            handleBillingInfo({
              name: cardData.fullName,
              address: cardData.address,
              zip: cardData.zip,
              country: cardData.country,
            });
          } else {
            toast({
              title: "Error",
              description:
                "This card was not accepted. Please check your card details and try again.",
              variant: "destructive",
            });
            console.error(response.messages.message);
          }
        }
      );
    } else {
      toast({
        title: "Error",
        description:
          "Cannot process credit cards with Authorize.net (Accept.js not loaded)",
        variant: "destructive",
      });
    }
  };

  const setCardField = (field: keyof CardInfo, value: string) => {
    setCard({ ...card, [field]: value });
    const fields = { ...card, [field]: value };
    const ready = [];

    if (
      Object.values(fields).every(
        (value) => value !== "" && value !== null && value !== undefined
      )
    ) {
      Object.entries(fields).forEach(([field, value]) => {
        if (field === "zip" && value.length >= 5) {
          ready.push(field);
        }
        if (field === "cardNumber" && value.length >= 16) {
          ready.push(field);
        }
        if (field === "expMonth" && value.length === 2) {
          ready.push(field);
        }
        if (field === "expYear" && value.length === 2) {
          ready.push(field);
        }
        if (field === "fullName" && value.length >= 2) {
          ready.push(field);
        }
        if (field === "address" && value.length > 5) {
          ready.push(field);
        }
        if (field === "cardCode" && value.length >= 3) {
          ready.push(field);
        }
      });
    }

    if (ready.length === 7) {
      tokenizeCard(fields);
    } else {
      handleToken("");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Label className="text-sm font-medium text-gray-800">
          Cardholder Name
        </Label>
        <Input
          id="fullName"
          name="fullName"
          value={card.fullName}
          onChange={(event) => setCardField("fullName", event.target.value)}
        />
      </div>

      <div className="grid grid-cols-12 gap-2 flex flex-row items-center gap-x-2">
        <div className="col-span-12 md:col-span-6 relative">
          <Label className="text-sm font-medium text-gray-800">
            Card Number
          </Label>
          <FontAwesomeIcon
            icon={faCreditCard}
            className="absolute left-2 top-10 md:top-8 text-muted-foreground h-4 w-4"
          />
          <Input
            id="cardNumber"
            name="cardNumber"
            inputMode="numeric"
            className="pl-10"
            value={card.cardNumber}
            onChange={(event) => {
              // Remove any non-digit characters
              const value = event.target.value.replace(/\D/g, "");
              // Add spaces after every 4 digits
              const formatted = value.replace(/(\d{4})(?=\d)/g, "$1 ");
              // Limit to 19 characters (16 digits + 3 spaces)
              const truncated = formatted.slice(0, 19);
              setCardField("cardNumber", truncated);
            }}
          />
        </div>
        <div className="col-span-4 md:col-span-2">
          <Label className="text-sm font-medium text-gray-800">Month</Label>
          <Input
            size={6}
            inputMode="numeric"
            id="expMonth"
            name="expMonth"
            placeholder="MM"
            maxLength={2}
            value={card.expMonth}
            onChange={(event) => setCardField("expMonth", event.target.value)}
          />
        </div>

        <div className="col-span-4 md:col-span-2">
          <Label className="text-sm font-medium text-gray-800">Year</Label>
          <Input
            size={6}
            inputMode="numeric"
            id="expYear"
            name="expYear"
            placeholder="YY"
            maxLength={2}
            value={card.expYear}
            onChange={(event) => setCardField("expYear", event.target.value)}
          />
        </div>
        <div className="col-span-4 md:col-span-2">
          <Label className="text-sm font-medium text-gray-800">CVV</Label>
          <Input
            size={8}
            inputMode="numeric"
            id="cardCode"
            name="cardCode"
            placeholder="CVV"
            maxLength={4}
            value={card.cardCode}
            onChange={(event) => setCardField("cardCode", event.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-8">
          <Label className="text-sm font-medium text-gray-800">
            Billing Address
          </Label>
          <Input
            id="address"
            name="address"
            placeholder="Street Address"
            value={card.address}
            onChange={(event) => setCardField("address", event.target.value)}
          />
        </div>
        <div className="col-span-4">
          <Label className="text-sm font-medium text-gray-800">Zipcode</Label>
          <Input
            inputMode="numeric"
            id="zip"
            name="zip"
            placeholder="Zipcode"
            value={card.zip}
            onChange={(event) => setCardField("zip", event.target.value)}
          />
        </div>
        {/* defaulting to US in the backend api call */}
        {/* may be we support foreign bank-issued cards in the future */}
        {/* <Select
          defaultValue="US"
          onValueChange={(value) => setCardField("country", value)}
          value={card.country}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="US">United States</SelectItem>
            <SelectItem value="CA">Canada</SelectItem>
            <SelectItem value="MX">Mexico</SelectItem>
          </SelectContent>
        </Select> */}
      </div>
    </div>
  );
}
